/**
 * Proximity-awareness helpers for the pod-room scene.
 *
 * When an agent emits a signal (or PM lands a decision) the other
 * characters glance toward them for a couple seconds before relaxing
 * back into ambient breathing.  Inspired by Gather / AI Town -- a
 * room feels alive when the seated characters react to each other.
 *
 * Pure functions only -- no Pixi dependency -- so they can be unit
 * tested without a renderer.
 */

/** Window during which a recent speaker pulls neighbours' attention.
 *  Eye + head-tilt magnitude decays linearly from full at 0 ms to
 *  zero at this deadline. */
export const LOOK_DURATION_MS = 2_500;

export interface LookSpeaker {
  actor: string;
  /** Canvas-space x of the speaker's seat (pixels). */
  x: number;
  /** Canvas-space y of the speaker's seat (pixels). */
  y: number;
  /** Age of the speaker's last event in ms (>= 0). */
  ageMs: number;
}

export interface LookOffset {
  /** Eye-shift in head-radius units (-0.15 .. +0.15 typical). */
  eyeX: number;
  eyeY: number;
  /** Head-icon rotation bias in radians, added to the ambient tilt. */
  tilt: number;
}

/**
 * Pick the freshest speaker that isn't ``selfActor`` and return an
 * offset that points the viewer's eyes (and slightly the head) at
 * that seat.  Returns ``null`` when nothing's worth looking at
 * (empty list, only self, or coincident seats).
 *
 * Magnitude fades with the speaker's age so the glance smoothly
 * relaxes; vertical eye shift is scaled to 0.8× horizontal because
 * pupils have less vertical travel than horizontal.
 */
export function pickLookOffset(
  selfActor: string,
  selfX: number,
  selfY: number,
  speakers: ReadonlyArray<LookSpeaker>,
): LookOffset | null {
  let best: LookSpeaker | null = null;
  for (const s of speakers) {
    if (s.actor === selfActor) continue;
    if (!best || s.ageMs < best.ageMs) best = s;
  }
  if (!best) return null;
  const dx = best.x - selfX;
  const dy = best.y - selfY;
  const len = Math.hypot(dx, dy);
  if (len < 1) return null;
  const decay = 1 - best.ageMs / LOOK_DURATION_MS;
  const eyeMag = 0.12 * decay;
  // Heads turn less than eyes -- otherwise the rotation reads
  // unnatural over a small character.
  const tiltMag = 0.10 * decay;
  return {
    eyeX: (dx / len) * eyeMag,
    eyeY: (dy / len) * eyeMag * 0.8,
    tilt: (dx / len) * tiltMag,
  };
}
